import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ArtisanBottomNav from "@/components/ArtisanBottomNav";
import ClientBottomNav from "@/components/ClientBottomNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Clock, CheckCircle, Archive, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";
import { useMyQuotes } from "@/hooks/useQuotes";
import type { Quote } from "@/hooks/useQuotes";
import { isQuoteExpired } from "@/hooks/useQuotes";
import { supabase } from "@/integrations/supabase/client";

const STATUS_CONFIG: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  sent:      { label: "En attente", variant: "secondary" },
  viewed:    { label: "Vu",         variant: "secondary" },
  accepted:  { label: "Accepté",    variant: "default" },
  rejected:  { label: "Refusé",     variant: "destructive" },
  expired:   { label: "Expiré",     variant: "outline" },
  cancelled: { label: "Annulé",     variant: "outline" },
};

type FilterKey = "pending" | "accepted" | "archived";

const filterQuote = (q: Quote, filter: FilterKey): boolean => {
  const exp = isQuoteExpired(q);
  if (filter === "pending") return (q.status === "sent" || q.status === "viewed") && !exp;
  if (filter === "accepted") return q.status === "accepted";
  if (filter === "archived")
    return q.status === "rejected" || q.status === "cancelled" || q.status === "expired" || exp;
  return true;
};

interface QuoteRowProps {
  quote: Quote;
  onClickConvo: (convoId: string) => void;
}

const QuoteRow = ({ quote, onClickConvo }: QuoteRowProps) => {
  const [otherName, setOtherName] = useState<string | null>(null);
  const lNavigate = useLocalizedNavigate();
  const expired = isQuoteExpired(quote);
  const displayStatus =
    (quote.status === "sent" || quote.status === "viewed") && expired ? "expired" : quote.status;
  const config = STATUS_CONFIG[displayStatus] || STATUS_CONFIG.sent;

  useEffect(() => {
    if (!quote.conversation_id) return;
    // On affiche le nom de la conversation (autre partie)
    supabase
      .from("conversations")
      .select("client_id, artisan_id, profiles!conversations_client_id_fkey(first_name, last_name)")
      .eq("id", quote.conversation_id)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        const profiles = (data as any).profiles;
        if (profiles) {
          setOtherName(`${profiles.first_name ?? ""} ${profiles.last_name ?? ""}`.trim() || "Client");
        }
      });
  }, [quote.conversation_id]);

  return (
    <div className="border rounded-xl p-4 bg-card flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-muted/30 transition-colors">
      <div className="flex items-start gap-3 min-w-0">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <FileText className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="font-display font-semibold text-sm truncate">{quote.title}</p>
          {otherName && <p className="text-xs text-muted-foreground mt-0.5">{otherName}</p>}
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <Badge variant={config.variant} className="text-[10px]">{config.label}</Badge>
            <span className="text-xs text-muted-foreground">
              {quote.total_amount.toLocaleString("fr-DZ")} DZD
            </span>
            <span className="text-xs text-muted-foreground">
              {new Date(quote.created_at).toLocaleDateString("fr-FR", {
                day: "numeric", month: "short", year: "numeric",
              })}
            </span>
          </div>
        </div>
      </div>
      {quote.conversation_id ? (
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 text-xs"
          onClick={() => onClickConvo(quote.conversation_id!)}
        >
          Voir la conversation
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 text-xs"
          onClick={() => lNavigate(`/devis/${quote.id}`)}
        >
          Voir le devis
        </Button>
      )}
    </div>
  );
};

const QuoteList = ({
  quotes,
  filter,
  emptyLabel,
  onClickConvo,
}: {
  quotes: Quote[];
  filter: FilterKey;
  emptyLabel: string;
  onClickConvo: (id: string) => void;
}) => {
  const filtered = quotes.filter((q) => filterQuote(q, filter));
  if (filtered.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
        <p className="text-sm">{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {filtered.map((q) => (
        <QuoteRow key={q.id} quote={q} onClickConvo={onClickConvo} />
      ))}
    </div>
  );
};

const MesDevis = () => {
  useSEO({ noindex: true });
  const { user, loading: authLoading } = useAuth();
  const lNavigate = useLocalizedNavigate();
  const { sentQuotes, receivedQuotes, loading } = useMyQuotes();
  const [activeFilter, setActiveFilter] = useState<FilterKey>("pending");

  useEffect(() => {
    if (!authLoading && !user) lNavigate("/connexion");
  }, [user, authLoading, lNavigate]);

  const handleClickConvo = (convoId: string) => {
    lNavigate(`/messagerie?conversation=${convoId}`);
  };

  if (authLoading) return null;

  const hasArtisanQuotes = sentQuotes.length > 0;
  const hasClientQuotes = receivedQuotes.length > 0;
  const showBothTabs = hasArtisanQuotes && hasClientQuotes;

  const filterTabs = [
    { key: "pending" as FilterKey, label: "En attente", icon: Clock },
    { key: "accepted" as FilterKey, label: "Acceptés", icon: CheckCircle },
    { key: "archived" as FilterKey, label: "Archivés", icon: Archive },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container max-w-2xl py-8 pb-24">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold flex items-center gap-2">
              <FileText className="h-6 w-6 text-primary" />
              Mes devis
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Gérez vos devis envoyés et reçus</p>
          </div>
          <Button
            variant="hero"
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={() => lNavigate("/devis")}
          >
            <Plus className="h-4 w-4" />
            Nouveau devis
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-xl p-4 flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            ))}
          </div>
        ) : !hasArtisanQuotes && !hasClientQuotes ? (
          <div className="py-16 text-center text-muted-foreground">
            <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
            <p className="font-display font-semibold mb-1">Aucun devis pour le moment</p>
            <p className="text-sm">Les devis apparaîtront ici une fois créés ou reçus.</p>
          </div>
        ) : (
          <>
            {/* Filtre par statut */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {filterTabs.map(({ key, label, icon: Icon }) => (
                <Button
                  key={key}
                  variant={activeFilter === key ? "default" : "outline"}
                  size="sm"
                  className="text-xs"
                  onClick={() => setActiveFilter(key)}
                >
                  <Icon className="h-3.5 w-3.5 me-1" /> {label}
                </Button>
              ))}
            </div>

            {showBothTabs ? (
              <Tabs defaultValue="sent">
                <TabsList className="mb-4">
                  <TabsTrigger value="sent">Devis envoyés ({sentQuotes.length})</TabsTrigger>
                  <TabsTrigger value="received">Devis reçus ({receivedQuotes.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="sent">
                  <QuoteList
                    quotes={sentQuotes}
                    filter={activeFilter}
                    emptyLabel="Aucun devis dans cette catégorie."
                    onClickConvo={handleClickConvo}
                  />
                </TabsContent>
                <TabsContent value="received">
                  <QuoteList
                    quotes={receivedQuotes}
                    filter={activeFilter}
                    emptyLabel="Aucun devis dans cette catégorie."
                    onClickConvo={handleClickConvo}
                  />
                </TabsContent>
              </Tabs>
            ) : hasArtisanQuotes ? (
              <>
                <p className="text-xs text-muted-foreground mb-3 font-medium">Devis envoyés</p>
                <QuoteList
                  quotes={sentQuotes}
                  filter={activeFilter}
                  emptyLabel="Aucun devis dans cette catégorie."
                  onClickConvo={handleClickConvo}
                />
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-3 font-medium">Devis reçus</p>
                <QuoteList
                  quotes={receivedQuotes}
                  filter={activeFilter}
                  emptyLabel="Aucun devis dans cette catégorie."
                  onClickConvo={handleClickConvo}
                />
              </>
            )}
          </>
        )}
      </main>
      <Footer />
      <ArtisanBottomNav />
      <ClientBottomNav />
    </div>
  );
};

export default MesDevis;
